import React, { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Radar, Radio, Compass, Gauge, Satellite, Wrench } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { Reveal } from "@/components/Reveal";

const ICONS = [Radar, Radio, Compass, Gauge, Satellite, Wrench];

function TiltCard({ Icon, index, title, desc }) {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rX = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 200, damping: 20 });
  const rY = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 200, damping: 20 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => { mx.set(0); my.set(0); };

  return (
    <Reveal delay={index * 0.07}>
      <motion.div
        ref={ref} onMouseMove={onMove} onMouseLeave={onLeave}
        style={{ rotateX: rX, rotateY: rY, transformPerspective: 900 }}
        className="group relative h-full bg-card border border-border rounded-2xl p-8 overflow-hidden hover:border-cyan/50 transition-colors"
        data-testid={`service-card-${index}`}
      >
        <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-cyan/10 blur-2xl group-hover:bg-cyan/20 transition-colors" />
        <span className="font-mono text-xs text-cyan">{String(index + 1).padStart(2, "0")}</span>
        <Icon className="w-9 h-9 text-brass mt-6 mb-5 group-hover:scale-110 transition-transform" />
        <h3 className="font-display text-2xl mb-3">{title}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
        <div className="mt-6 h-px w-0 bg-cyan group-hover:w-full transition-all duration-500" />
      </motion.div>
    </Reveal>
  );
}

export default function Services() {
  const { t } = useApp();
  return (
    <section id="services" className="relative py-28 lg:py-40 bg-background" data-testid="services-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <Reveal>
          <h2 className="font-display text-4xl md:text-6xl tracking-tight max-w-3xl leading-[1.05]">{t.services.title}</h2>
          <p className="mt-6 max-w-xl text-muted-foreground leading-relaxed">{t.services.sub}</p>
        </Reveal>
        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {t.services.items.map((s, i) => (
            <TiltCard key={s.t} Icon={ICONS[i]} index={i} title={s.t} desc={s.d} />
          ))}
        </div>
      </div>
    </section>
  );
}
